import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiService } from 'api';
import type { FirmCompanies } from 'api/models/FirmCompanies';
import type { FirmUserPermission } from 'api/models/FirmUserPermission';
import {
  CAP_TABLE_MAPPING_COMPLETED,
  CAP_TABLE_MAPPING_FAILED,
  CAP_TABLE_MAPPING_PROCESSING,
  CAP_TABLE_MAPPING_STARTED,
  DOCUMENT_UPLOADED,
  FINANCIAL_MAPPING_COMPLETED,
  FINANCIAL_MAPPING_FAILED,
  FINANCIAL_MAPPING_PROCESSING,
  FINANCIAL_MAPPING_STARTED,
  GENERATION_JOB_COMPLETED,
  GENERATION_JOB_FAILED,
  GENERATION_JOB_PROCESSING,
  GENERATION_JOB_STARTED,
  PARSING_COMPLETED,
  PARSING_FAILED,
  PARSING_PROCESSING,
  PARSING_STARTED,
  PDF_PROCESSING_COMPLETED,
  PDF_PROCESSING_FAILED,
  PDF_PROCESSING_PROCESSING,
  PDF_PROCESSING_STARTED
} from 'common/constants/notificationEventTypes';
import { clientWithAuth } from 'dashboard409a/services/base';

export type { FirmCompanies, FirmUserPermission };

const NOTIFICATION_PREFERENCES_URL = '/api/notification-preferences';
// Backend ViewSet.update() maps to PUT /{pk} — pk is ignored server-side
const NOTIFICATION_PREFERENCES_UPDATE_URL = '/api/notification-preferences/0';
const QUERY_KEY = ['notification-preferences'] as const;

/** Matches backend NotificationPreferenceSerializer exactly. */
export interface NotificationPreferences {
  excluded_event_types: string[];
  all_companies: boolean;
  excluded_firms: number[];
  excluded_companies: number[];
}

export const ALL_EVENT_TYPES = [
  GENERATION_JOB_STARTED,
  GENERATION_JOB_PROCESSING,
  GENERATION_JOB_COMPLETED,
  GENERATION_JOB_FAILED,
  CAP_TABLE_MAPPING_STARTED,
  CAP_TABLE_MAPPING_PROCESSING,
  CAP_TABLE_MAPPING_COMPLETED,
  CAP_TABLE_MAPPING_FAILED,
  FINANCIAL_MAPPING_STARTED,
  FINANCIAL_MAPPING_PROCESSING,
  FINANCIAL_MAPPING_COMPLETED,
  FINANCIAL_MAPPING_FAILED,
  PDF_PROCESSING_STARTED,
  PDF_PROCESSING_PROCESSING,
  PDF_PROCESSING_COMPLETED,
  PDF_PROCESSING_FAILED,
  PARSING_STARTED,
  PARSING_PROCESSING,
  PARSING_COMPLETED,
  PARSING_FAILED,
  DOCUMENT_UPLOADED,
] as const;

export type NotificationEventType = (typeof ALL_EVENT_TYPES)[number];

/** Categories for grouping event types in the UI. */
export const EVENT_CATEGORIES: { label: string; events: readonly string[] }[] = [
  {
    label: 'Generation Jobs',
    events: [GENERATION_JOB_STARTED, GENERATION_JOB_PROCESSING, GENERATION_JOB_COMPLETED, GENERATION_JOB_FAILED],
  },
  {
    label: 'Cap Table Mapping',
    events: [CAP_TABLE_MAPPING_STARTED, CAP_TABLE_MAPPING_PROCESSING, CAP_TABLE_MAPPING_COMPLETED, CAP_TABLE_MAPPING_FAILED],
  },
  {
    label: 'Financial Mapping',
    events: [FINANCIAL_MAPPING_STARTED, FINANCIAL_MAPPING_PROCESSING, FINANCIAL_MAPPING_COMPLETED, FINANCIAL_MAPPING_FAILED],
  },
  {
    label: 'PDF Processing',
    events: [PDF_PROCESSING_STARTED, PDF_PROCESSING_PROCESSING, PDF_PROCESSING_COMPLETED, PDF_PROCESSING_FAILED],
  },
  {
    label: 'Parsing',
    events: [PARSING_STARTED, PARSING_PROCESSING, PARSING_COMPLETED, PARSING_FAILED],
  },
  {
    label: 'Document Uploaded',
    events: [DOCUMENT_UPLOADED],
  }
];

/**
 * All firms the current user has access to.
 * Used to populate the firm selector in notification preferences.
 * Only fetches when `enabled` is true.
 */
export const useNotificationFirms = (enabled = true) =>
  useQuery<FirmUserPermission[]>({
    queryKey: ['notification-preferences', 'firms'],
    queryFn: async () => {
      const res = await ApiService.apiFirmsList() as unknown as { results: FirmUserPermission[] } | FirmUserPermission[];
      return Array.isArray(res) ? res : res.results;
    },
    staleTime: 10 * 60 * 1000,
    enabled,
  });

/**
 * Companies for a specific firm.
 * Used to populate the company list in notification preferences after a firm is selected.
 * Only fetches when `enabled` is true and `firmId` is non-null.
 */
export const useNotificationFirmCompanies = (firmId: number | null, enabled = true) =>
  useQuery<FirmCompanies[]>({
    queryKey: ['notification-preferences', 'firm-companies', firmId],
    queryFn: async () => {
      const res = await ApiService.apiFirmsCompaniesList(firmId!.toString()) as unknown as { results: FirmCompanies[] } | FirmCompanies[];
      return Array.isArray(res) ? res : res.results;
    },
    staleTime: 5 * 60 * 1000,
    enabled: enabled && firmId !== null,
  });

export const useNotificationPreferences = () =>
  useQuery<NotificationPreferences>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await clientWithAuth.get<NotificationPreferences>(NOTIFICATION_PREFERENCES_URL);
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation<NotificationPreferences, Error, Partial<NotificationPreferences>>({
    mutationFn: async data => {
      const current = queryClient.getQueryData<NotificationPreferences>(QUERY_KEY);
      const payload: NotificationPreferences = {
        excluded_event_types: current?.excluded_event_types ?? [],
        all_companies: current?.all_companies ?? true,
        excluded_firms: current?.excluded_firms ?? [],
        excluded_companies: current?.excluded_companies ?? [],
        ...data,
      };
      const res = await clientWithAuth.put<NotificationPreferences>(NOTIFICATION_PREFERENCES_UPDATE_URL, payload);
      return res.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });
};
